import React, { useEffect, useMemo, useState } from "react";
import { SmallText, XsmallText } from "../../lib/ui/components/typography/Typography";
import Toggle from "../../lib/ui/components/toggle/Toggle";
import Button from "../../lib/ui/components/button/Button";
import { GET_JOB_ATS_STAGES } from "../../lib/urls";
import { mergeStageOptionsWithStale, initialEligibleSelection } from "../../utils/jobStageSettings";

export const AUTO_INTERVIEW_INVITE_SCOPE = {
    NEW_ONLY: "new_only",
    INCLUDE_EXISTING: "include_existing",
};

/**
 * Content for the “enable automatic interviews” confirmation: stage toggles + scope tiles.
 */
export default function AutomaticInterviewEnableDetails({
    value,
    onChange,
    companyId,
    jobId,
    token,
    atsJobId,
    initialEligibleStages,
    selectedStages,
    onSelectedStagesChange,
    onReadyChange,
}) {
    const [liveStages, setLiveStages] = useState([]);
    const [loading, setLoading] = useState(Boolean(atsJobId));
    const [error, setError] = useState("");

    const fetchStages = async () => {
        if (!atsJobId || !companyId || !jobId || !token) {
            setLoading(false);
            onReadyChange?.({ loading: false, error: "", canConfirm: true });
            return;
        }
        setLoading(true);
        setError("");
        try {
            const response = await fetch(GET_JOB_ATS_STAGES(companyId, jobId), {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) {
                throw new Error("Failed to load ATS stages");
            }
            const data = await response.json();
            const stages = data.stages || [];
            setLiveStages(stages);
            const preset = initialEligibleSelection(initialEligibleStages);
            onSelectedStagesChange?.(preset);
        } catch (err) {
            setError(err.message || "Failed to load ATS stages");
            setLiveStages([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchStages();
    }, [atsJobId, companyId, jobId, token]);

    const { live, stale, all } = useMemo(
        () => mergeStageOptionsWithStale(liveStages, initialEligibleStages),
        [liveStages, initialEligibleStages]
    );

    const selectedSet = useMemo(() => new Set(selectedStages || []), [selectedStages]);
    const canConfirm = !atsJobId || selectedSet.size > 0;

    useEffect(() => {
        onReadyChange?.({
            loading,
            error,
            canConfirm: !loading && !error && canConfirm,
        });
    }, [loading, error, canConfirm, onReadyChange]);

    const tiles = [
        {
            id: AUTO_INTERVIEW_INVITE_SCOPE.NEW_ONLY,
            label: "New applicants only",
            description:
                "Only automatically invite candidates added after you confirm or add questions (if none exist).",
        },
        {
            id: AUTO_INTERVIEW_INVITE_SCOPE.INCLUDE_EXISTING,
            label: "Include eligible existing applicants",
            description:
                "Also send invitations to currently added applicants who have not received an invite yet.",
        },
    ];

    const toggleStage = (name, next) => {
        const current = new Set(selectedStages || []);
        if (next) current.add(name);
        else current.delete(name);
        onSelectedStagesChange?.(Array.from(current));
    };

    return (
        <div className="space-y-4">
            <SmallText className="m-0">
                When automatic interviews are on, candidates without an interview invite will receive invitations automatically.
                If you do not have any questions yet, candidates will be automatically invited after you add questions.
            </SmallText>
            {atsJobId ? (
                <div className="space-y-3">
                    <SmallText className="m-0" weight="semibold" emphasis={true}>
                        Which ATS stages should receive automatic interviews?
                    </SmallText>
                    {loading ? (
                        <XsmallText>Loading stages from your ATS…</XsmallText>
                    ) : error ? (
                        <div className="space-y-2">
                            <XsmallText className="text-red-600">{error}</XsmallText>
                            <Button variant="secondary" onClick={() => void fetchStages()}>
                                Retry
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {all.map((stage) => (
                                <div key={stage} className="flex items-start gap-3">
                                    <Toggle
                                        checked={selectedSet.has(stage)}
                                        onChange={(next) => toggleStage(stage, next)}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <SmallText emphasis={true}>{stage}</SmallText>
                                        {stale.includes(stage) && !live.includes(stage) ? (
                                            <XsmallText>No longer in this role’s ATS pipeline</XsmallText>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                            {all.length === 0 ? (
                                <XsmallText>No ATS stages were returned for this role.</XsmallText>
                            ) : null}
                            {selectedSet.size > 0 ? (
                                <XsmallText>
                                    Selected: {Array.from(selectedSet).join(", ")}
                                </XsmallText>
                            ) : (
                                <XsmallText>
                                    Turn on at least one stage to enable automatic interviews.
                                </XsmallText>
                            )}
                        </div>
                    )}
                </div>
            ) : null}
            <SmallText className="m-0" weight="semibold" emphasis={true}>
                Who should get invitations?
            </SmallText>
            <div className="grid gap-3">
                {tiles.map((tile) => {
                    const selected = value === tile.id;
                    return (
                        <label
                            key={tile.id}
                            className={[
                                "flex cursor-pointer flex-col gap-2 rounded-lg border p-4 text-left transition-colors",
                                selected
                                    ? "border-primary bg-purple-50/80 ring-1 ring-primary"
                                    : "border-gray-200 bg-white hover:border-gray-300",
                            ].join(" ")}
                            data-cy={`auto-interview-scope-${tile.id}`}
                        >
                            <div className="flex items-start gap-3">
                                <input
                                    type="radio"
                                    name="autoInterviewInviteScope"
                                    value={tile.id}
                                    checked={selected}
                                    onChange={() => onChange(tile.id)}
                                    className="mt-1 h-4 w-4 shrink-0 border-gray-300 text-primary focus:ring-primary"
                                />
                                <div className="min-w-0">
                                    <SmallText emphasis={true} weight="semibold">
                                        {tile.label}
                                    </SmallText>
                                    <XsmallText>
                                        {tile.description}
                                    </XsmallText>
                                </div>
                            </div>
                        </label>
                    );
                })}
            </div>
            <XsmallText className="m-0">
                Cancel to keep automatic interviews off, or confirm to turn them on.
            </XsmallText>
        </div>
    );
}
